import { pixelRatio2d } from '@/editor/math/const';
import {
  ENTITY_PRIORITY,
  ENTITY_RENDER_ORDER,
  errorHighlightedColor,
  hoverOutlineColor,
  selectedOutlineColor,
  selectedSeparateWallBGColor,
  separateWallBGColor,
  separateWallOutline,
  virtualSeparateWallLineColor
} from '@/editor/constant/entity';
import { EEntityType } from '@/editor/types';
import BaseModel2d from './BaseModel2d';
import { globalStores } from '@/editor/stores';
import { EContainerType, EEntityStatus2 } from '@/editor/types/entity';
import { Mesh, MeshBasicMaterial, ShapeGeometry } from 'three';
import { ESeparateWallType, SeparateWallData } from '@/editor/data/separateWall/SeparateWall';
import { calAuxiliarySegs } from '@/editor/data/separateWall/utils';
import { LineMaterial, LineSegments2, LineSegmentsGeometry } from 'three/examples/jsm/Addons.js';
import { getBaseShape, sampleShapePath } from '@/editor/utils/wallShape';
import { DEFAULT_SEP_WALL_THICKNESS } from '@/editor/constant/wall';
import { pathToGmtShape } from '@/editor/utils/shape';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';

const DASH_BG = new MeshBasicMaterial({ transparent: true, opacity: 0, userData: { shared: true } });
const SEPARATE_WALL_MATERIAL = {
  outline: {
    [EEntityStatus2.Default]: new LineMaterial({
      color: separateWallOutline,
      userData: { shared: true }
    }),
    [EEntityStatus2.DrawNormal]: new LineMaterial({
      color: selectedOutlineColor,
      userData: { shared: true }
    }),
    [EEntityStatus2.DrawUnnormal]: new LineMaterial({
      color: errorHighlightedColor,
      userData: { shared: true }
    }),
    [EEntityStatus2.Hover]: new LineMaterial({
      color: hoverOutlineColor,
      userData: { shared: true }
    }),
    [EEntityStatus2.Selected]: new LineMaterial({
      color: selectedOutlineColor,
      userData: { shared: true }
    })
  },
  bg: {
    [EEntityStatus2.Default]: new MeshBasicMaterial({
      color: separateWallBGColor,
      userData: { shared: true }
    }),
    [EEntityStatus2.DrawNormal]: new MeshBasicMaterial({
      opacity: 0.6,
      color: separateWallBGColor,
      transparent: true,
      userData: { shared: true }
    }),
    [EEntityStatus2.DrawUnnormal]: new MeshBasicMaterial({
      opacity: 0.6,
      color: separateWallBGColor,
      transparent: true,
      userData: { shared: true }
    }),
    [EEntityStatus2.Hover]: new MeshBasicMaterial({
      opacity: 0,
      transparent: true,
      userData: { shared: true }
    }),
    [EEntityStatus2.Selected]: new MeshBasicMaterial({
      color: selectedSeparateWallBGColor,
      userData: { shared: true }
    })
  },
  dashLine: {
    [EEntityStatus2.Default]: new LineMaterial({
      color: virtualSeparateWallLineColor,
      linewidth: 1,
      userData: { shared: true }
    }),
    [EEntityStatus2.DrawNormal]: new LineMaterial({
      color: selectedOutlineColor,
      linewidth: 1,
      userData: { shared: true }
    }),
    [EEntityStatus2.DrawUnnormal]: new LineMaterial({
      color: errorHighlightedColor,
      linewidth: 1,
      userData: { shared: true }
    }),
    [EEntityStatus2.Hover]: new LineMaterial({
      color: hoverOutlineColor,
      linewidth: 1,
      userData: { shared: true }
    }),
    [EEntityStatus2.Selected]: new LineMaterial({
      color: selectedOutlineColor,
      linewidth: 1,
      userData: { shared: true }
    })
  },
  dashBg: {
    [EEntityStatus2.Default]: DASH_BG,
    [EEntityStatus2.DrawNormal]: DASH_BG,
    [EEntityStatus2.DrawUnnormal]: DASH_BG,
    [EEntityStatus2.Hover]: DASH_BG,
    [EEntityStatus2.Selected]: DASH_BG
  }
};

function pushLineSegmentPositions(segs: number[], start: IPoint2d, end: IPoint2d) {
  segs.push(start.x / pixelRatio2d, start.y / pixelRatio2d, 0, end.x / pixelRatio2d, end.y / pixelRatio2d, 0);
}

function pushArcSegmentPositions(segs: number[], arc: ArcSeg2d) {
  const points = arc.getPoints();
  for (let i = 0; i < points.length - 1; i++) {
    pushLineSegmentPositions(segs, points[i], points[i + 1]);
  }
}

function pushPathSegmentPositions(segs: number[], path: IPoint2d[]) {
  for (let i = 0; i < path.length; i++) {
    pushLineSegmentPositions(segs, path[i], path[(i + 1) % path.length]);
  }
}

class SeparateWallModel extends BaseModel2d {
  protected _status: EEntityStatus2 = EEntityStatus2.Default;
  protected _outlineModel: LineSegments2 | null = null;
  protected _bgModel: Mesh | null = null;
  protected _isDashLine: boolean = false;

  constructor(params: { id?: string; intersectable?: boolean }) {
    super();

    this._modelGroup.userData.type = EEntityType.SeparateWall;
    this._modelGroup.userData.entityId = params.id;
    this._modelGroup.userData.priority = ENTITY_PRIORITY.separateWall;
    this._modelGroup.userData.containerType = EContainerType.entity;

    const intersectable = params.intersectable ?? true;
    if (intersectable) {
      globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup.add(this._modelGroup);
    } else {
      globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);
    }
  }

  private getStatusToRenderOrder() {
    switch (this._status) {
      case EEntityStatus2.DrawNormal:
      case EEntityStatus2.DrawUnnormal:
      case EEntityStatus2.Hover:
        return ENTITY_RENDER_ORDER.renderOrderTemp;
      default:
        return ENTITY_RENDER_ORDER.separateWall;
    }
  }

  createModel(separateWallData: SeparateWallData) {
    this.clear();

    const drawShape = separateWallData.drawSeparateWallInfo.drawShape;

    if (this._isDashLine) {
      const { baseShape } = getBaseShape({ ...separateWallData, thickness: DEFAULT_SEP_WALL_THICKNESS });
      this.createBgModel(sampleShapePath(baseShape)); // 虚线需要对背景特殊处理
      this.createDashLineModel(separateWallData);
    } else {
      const sampledDrawShape = sampleShapePath(drawShape);
      this.createBgModel(sampledDrawShape);
      this.createOutlineModel(sampledDrawShape, separateWallData);
    }

    const renderOrder = this.getStatusToRenderOrder();
    this.setDisplayOrder(renderOrder);
    if (this._outlineModel) {
      this._outlineModel.renderOrder = renderOrder + 1;
      this._outlineModel.userData.containerType = EContainerType.noIntersectable; // 线条不参与碰撞检测
    }
  }

  protected createOutlineModel(points: IPoint2d[], separateWallData: SeparateWallData) {
    const lineGeometry = new LineSegmentsGeometry();
    const segs: number[] = [];

    pushPathSegmentPositions(segs, points);

    const auxiliarySegs = calAuxiliarySegs(separateWallData);
    for (const seg of auxiliarySegs) {
      if (seg instanceof ArcSeg2d) {
        pushArcSegmentPositions(segs, seg);
      } else {
        pushLineSegmentPositions(segs, seg.start, seg.end);
      }
    }

    lineGeometry.setPositions(segs);
    const material = SEPARATE_WALL_MATERIAL.outline[this._status];
    const line = new LineSegments2(lineGeometry, material);
    this._outlineModel = line;
    this._modelGroup.add(line);
  }

  protected createDashLineModel(separateWallData: SeparateWallData) {
    const lineGeometry = new LineSegmentsGeometry();
    const segs: number[] = [];

    if (separateWallData.center) {
      pushArcSegmentPositions(
        segs,
        new ArcSeg2d({
          start: separateWallData.start,
          end: separateWallData.end,
          center: separateWallData.center,
          clockwise: separateWallData.clockwise ?? false
        })
      );
    } else {
      pushLineSegmentPositions(segs, separateWallData.start, separateWallData.end);
    }

    if (!segs.length) return;
    lineGeometry.setPositions(segs);

    const material = SEPARATE_WALL_MATERIAL.dashLine[this._status];
    const line = new LineSegments2(lineGeometry, material);
    this._outlineModel = line;
    this._modelGroup.add(line);
  }

  protected createBgModel(points: IPoint2d[]) {
    if (!points.length) return;

    const shape = pathToGmtShape(points);
    const geometry = new ShapeGeometry(shape);
    const material = this._isDashLine
      ? SEPARATE_WALL_MATERIAL.dashBg[this._status]
      : SEPARATE_WALL_MATERIAL.bg[this._status];
    const mesh = new Mesh(geometry, material);
    this._bgModel = mesh;
    this._modelGroup.add(mesh);
  }

  updateSeparateWall(separateWallData: SeparateWallData & { status?: EEntityStatus2 }) {
    if (separateWallData.status) this._status = separateWallData.status;
    this._isDashLine = separateWallData.type === ESeparateWallType.VirtualWall;

    this.createModel(separateWallData);
  }

  setStatus(status: EEntityStatus2) {
    if (!this._outlineModel || !this._bgModel) return;

    this._status = status;

    this._bgModel.material = this._isDashLine
      ? SEPARATE_WALL_MATERIAL.dashBg[this._status]
      : SEPARATE_WALL_MATERIAL.bg[this._status];
    this._outlineModel.material = this._isDashLine
      ? SEPARATE_WALL_MATERIAL.dashLine[this._status]
      : SEPARATE_WALL_MATERIAL.outline[this._status];
  }

  override setVisible(visible: boolean) {
    this._modelGroup.visible = visible;
  }
}

export default SeparateWallModel;
